import { useQueries, useQuery } from '@tanstack/react-query'
import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { formatMinor } from '~/api/client'
import type { AllocationMode } from '~/charts/AllocationChart'
import { AllocationChart } from '~/charts/AllocationChart'
import type { LegendKey } from '~/charts/ChartCard'
import { ChartCard } from '~/charts/ChartCard'
import type { AllocationCutId, AllocationTimeline, CategoryDrift } from '~/charts/allocation'
import {
  ALLOCATION_CUTS,
  DEFAULT_CUT,
  bucketHoldings,
  buildTimeline,
  cutsWithMoreThanOneBand,
  driftOver,
} from '~/charts/allocation'
import { fetchHoldings, fetchNetWorthSeries, isNoData } from '~/charts/data'
import { formatDriftBp, formatShareBp } from '~/charts/format'
import { DEFAULT_RANGE, RANGE_PRESETS, sampleDates, sliceRange } from '~/charts/series'
import { chartCss, seriesColor } from '~/charts/theme'
import { Card, CardHead } from '~/components/ui/Card'
import { Filter } from '~/components/ui/Controls'
import { DataTable, Swatch } from '~/components/ui/Table'

/** Two dozen points is enough to see a year of drift without the range setting the cost. */
const MAX_SAMPLES = 24

const SHOW_CHOICES = [
  { id: 'share', label: 'Share' },
  { id: 'value', label: 'Value' },
]

const DRIFT_COLUMNS = [
  { id: 'category', label: 'Category' },
  { id: 'now', label: 'Share now', width: '34%' },
  { id: 'value', label: 'Value now', align: 'right' as const },
  { id: 'then', label: 'Then', align: 'right' as const },
  { id: 'drift', label: 'Drift', align: 'right' as const },
]

export function Allocation(): ReactElement {
  const [rangeId, setRangeId] = useState<string>(DEFAULT_RANGE)
  const [cutId, setCutId] = useState<AllocationCutId>(DEFAULT_CUT)
  const [mode, setMode] = useState<AllocationMode>('share')
  const [view, setView] = useState<'chart' | 'table'>('chart')

  const series = useQuery({ queryKey: ['networth', 'series'], queryFn: fetchNetWorthSeries, retry: false })
  const baseCurrency = series.data?.baseCurrency ?? 'DKK'
  const holdsCompany = series.data?.holdsThroughCompany ?? false

  const dates = useMemo(
    () => sampleDates(sliceRange(series.data?.points ?? [], rangeId).map((p) => p.date), MAX_SAMPLES),
    [series.data, rangeId],
  )

  const holdings = useQueries({
    queries: dates.map((date) => ({
      queryKey: ['holdings', date],
      queryFn: () => fetchHoldings(date),
      retry: false,
      staleTime: Number.POSITIVE_INFINITY,
    })),
  })

  const loaded = holdings.flatMap((result) => (result.data ? [result.data] : []))
  const pending = holdings.some((result) => result.isPending)
  const failed = holdings.find((result) => result.isError)
  // Judged only once every sampled date has answered: a cut read off part of the
  // range can look like one band where the rest would have split it.
  const settled = !pending && !failed && loaded.length > 0
  const telling = settled ? cutsWithMoreThanOneBand(loaded) : ALLOCATION_CUTS
  // Everything in one place: no cut can split it, so there is nothing to choose
  // between and nothing worth drawing. The full list is kept underneath only so
  // the range line above the page still has a cut to count by.
  const nothingToBreakDown = telling.length === 0
  const offered = nothingToBreakDown ? ALLOCATION_CUTS : telling
  // Derived rather than stored, so the choice shown as selected is always one
  // this range can actually draw.
  const cut = offered.find((one) => one.id === cutId) ?? offered[0]
  const timeline = buildTimeline(
    loaded.map((response) => bucketHoldings(response, cut)),
    cut,
  )
  const drift = driftOver(timeline)

  return (
    <main className="nw nw-allocation">
      <style>{chartCss}</style>
      <h1>Allocation</h1>
      {/* What is being counted and over how much of the range — the cut's own
          sentence belongs on the card the cut is drawn in. */}
      {series.data && timeline.rows.length > 0 && (
        <p className="nw-sub">
          {`${timeline.rows.length} ${timeline.rows.length === 1 ? 'date' : 'dates'} sampled `}
          {`· ${timeline.categories.length} categories · all figures in ${baseCurrency}`}
        </p>
      )}

      {series.isError && isNoData(series.error) && (
        <p className="nw-empty">No snapshot has been taken yet, so there is nothing to break down.</p>
      )}

      {series.isError && !isNoData(series.error) && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not load the date range: {series.error.message}</span>
        </p>
      )}

      {series.data && (
        <>
          <div className="nw-filters">
            <Filter
              label="Range"
              choices={RANGE_PRESETS.map((preset) => ({ id: preset.id, label: preset.label }))}
              value={rangeId}
              onChange={setRangeId}
            />
            {!nothingToBreakDown && (
              <>
                <Filter
                  label="Cut"
                  choices={offered.map((option) => ({ id: option.id, label: option.label }))}
                  value={cut.id}
                  onChange={(id) => setCutId(id as AllocationCutId)}
                />
                <Filter
                  label="Show"
                  choices={SHOW_CHOICES}
                  value={mode}
                  onChange={(id) => setMode(id as AllocationMode)}
                />
              </>
            )}
          </div>

          {failed && (
            <p className="nw-flag nw-flag--missing">
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>
                {holdings.filter((r) => r.isError).length} of {dates.length} dates could not be
                loaded, so the shape below is incomplete: {failed.error?.message}
              </span>
            </p>
          )}

          {nothingToBreakDown ? (
            <p className="nw-empty">Everything is in one place, so there is nothing to break down yet.</p>
          ) : (
            <>
            <ChartCard
              title={`${cut.label} over time`}
              description={`${cut.description} ${
                mode === 'share'
                  ? 'Share of the holdings that carry a price, sampled across the range.'
                  : 'Value of the holdings that carry a price, sampled across the range.'
              }`}
              view={view}
              onViewChange={setView}
              legend={legendFor(timeline)}
            >
              {timeline.rows.length === 0 ? (
                <p className="nw-empty">{pending ? 'Loading…' : 'Nothing to break down here.'}</p>
              ) : view === 'chart' ? (
                <div className={pending ? 'nw-plot nw-plot--loading' : 'nw-plot'}>
                  <AllocationChart
                    categories={timeline.categories}
                    rows={timeline.rows}
                    mode={mode}
                    baseCurrency={baseCurrency}
                  />
                </div>
              ) : (
                <TimelineTable timeline={timeline} mode={mode} baseCurrency={baseCurrency} />
              )}
            </ChartCard>

            {drift.length > 0 && (
              <Card>
                <CardHead
                  title="Drift over the range"
                  lede={`Where each share started and where it ended — ${timeline.rows[0]?.date} to ${timeline.rows[timeline.rows.length - 1]?.date}.`}
                />
                <DriftTable drift={drift} baseCurrency={baseCurrency} />
              </Card>
            )}

            {timeline.unpricedSymbols.length > 0 && (
              <p className="nw-flag nw-flag--missing">
                <span className="nw-flag-icon" aria-hidden="true">
                  ✕
                </span>
                <span>
                  No price for {timeline.unpricedSymbols.join(', ')}. Those holdings are left out of
                  every share above rather than counted as zero.
                </span>
              </p>
            )}

            {timeline.unclassifiedSymbols.length > 0 && (
              <p className="nw-flag nw-flag--stale">
                <span className="nw-flag-icon" aria-hidden="true">
                  ▲
                </span>
                <span>
                  This cut cannot place {timeline.unclassifiedSymbols.join(', ')}, so they are missing
                  from it.
                </span>
              </p>
            )}

            {/* The qualification is only a qualification where there is a tax to
                be charged. Without a second entity holding something, nothing is
                ever taken out of these shares, so naming the tax describes an
                arrangement the reader does not have. */}
            <p className="nw-note">
              Sampled on {timeline.rows.length} {timeline.rows.length === 1 ? 'date' : 'dates'} across
              the range. Every share is of the priced holdings on that date
              {holdsCompany ? ', before extraction tax' : ''}.
            </p>
            </>
          )}
        </>
      )}
    </main>
  )
}

function legendFor(timeline: AllocationTimeline): LegendKey[] {
  return timeline.categories.map((category) => ({
    label: category.label,
    color: seriesColor(category.slot),
    shape: 'area' as const,
  }))
}

function DriftTable({
  drift,
  baseCurrency,
}: {
  drift: readonly CategoryDrift[]
  baseCurrency: string
}): ReactElement {
  const sum = (of: (row: CategoryDrift) => number): number =>
    drift.reduce((total, row) => total + of(row), 0)

  return (
    <DataTable
      columns={DRIFT_COLUMNS}
      label="Drift over the range"
      total={
        <tr>
          {/* Every category is every priced holding, so the shares are of each
              other and come to a hundred by construction. A column of parts is
              only readable once the whole they are parts of is on the page. */}
          <td>All categories</td>
          <td>
            <span className="nw-share">
              <span className="nw-bar">
                <i style={{ background: 'var(--nw-faint)', width: '100%' }} />
              </span>
              <b>{formatShareBp(sum((row) => row.endShareBp))}</b>
            </span>
          </td>
          <td className="nw-num">{formatMinor(sum((row) => row.endValueMinor), baseCurrency)}</td>
          <td className="nw-num">{formatShareBp(sum((row) => row.startShareBp))}</td>
          <td className="nw-num">{formatDriftBp(sum((row) => row.driftBp))}</td>
        </tr>
      }
    >
      {drift.map((row) => (
        <tr key={row.key}>
          <td>
            <span className="nw-measure">
              <Swatch color={seriesColor(row.slot)} />
              {row.label}
            </span>
          </td>
          <td>
            <span className="nw-share">
              <span className="nw-bar">
                <i style={{ background: seriesColor(row.slot), width: `${row.endShareBp / 100}%` }} />
              </span>
              <b>{formatShareBp(row.endShareBp)}</b>
            </span>
          </td>
          <td className="nw-num">{formatMinor(row.endValueMinor, baseCurrency)}</td>
          <td className="nw-num">{formatShareBp(row.startShareBp)}</td>
          <td className="nw-num">{formatDriftBp(row.driftBp)}</td>
        </tr>
      ))}
    </DataTable>
  )
}

function TimelineTable({
  timeline,
  mode,
  baseCurrency,
}: {
  timeline: AllocationTimeline
  mode: AllocationMode
  baseCurrency: string
}): ReactElement {
  return (
    <div className="nw-table-wrap">
      <DataTable
        dense
        label={`${mode === 'share' ? 'Share' : 'Value'} by category and date`}
        columns={[
          { id: 'date', label: 'Date', width: '120px' },
          ...timeline.categories.map((category) => ({
            id: category.key,
            label: category.label,
            align: 'right' as const,
          })),
        ]}
      >
        {[...timeline.rows].reverse().map((row) => (
          <tr key={row.date} data-flagged={row.flaggedMinor > 0}>
            <td>{row.date}</td>
            {timeline.categories.map((category) => (
              <td className="nw-num" key={category.key}>
                {mode === 'share'
                  ? formatShareBp(row.shareBp[category.key] ?? 0)
                  : formatMinor(row.valueMinor[category.key] ?? 0, baseCurrency)}
              </td>
            ))}
          </tr>
        ))}
      </DataTable>
    </div>
  )
}
